import type { AxiosInstance } from 'axios';
import type { ApiDataList } from 'src/types/common';
import type { ApiListingItem, ApiGetListingsPayload } from 'src/types/listing';

import { useQuery, queryOptions, keepPreviousData } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { askUnlessServerAnswered } from './serverRendered';

/**
 * One page of the sale catalog as a query: `GET /buyer/listings` with the page's `limit` and
 * `offset` (built by `toSaleParams`, `src/utils/catalogUrl.ts`). The hook below asks it from the
 * browser; the server asks the same question — the same key, through its own client — for the page
 * it renders (`src/server/catalog.server.ts`).
 */
export const listingsPageQuery = (params: ApiGetListingsPayload, http: Pick<AxiosInstance, 'get'> = axios) =>
  queryOptions({
    queryKey: QueryKeys.listings(params),
    queryFn: async (): Promise<ApiDataList<ApiListingItem>> => {
      const response = await http.get<ApiDataList<ApiListingItem>>('/buyer/listings', {
        params,
        // A feed that never answers becomes "taking longer" instead of a spinner without end.
        timeout: READ_TIMEOUT_MS,
      });
      return response.data;
    },
    staleTime: LIVE_STALE_TIME,
  });

/**
 * One page of the sale catalog. The page is in the address, so a page is a query of its own — not
 * a list that grows, as the "Load more" feed was.
 *
 * While the next page is on its way the previous one stays on screen (`keepPreviousData`): a page
 * link swaps the cards in place instead of dropping the grid to a spinner and back, which is a jump
 * of the whole layout (SF-I2). `enabled: false` sends nothing — an address naming a make the
 * catalog does not know.
 */
export function useListingsPage(params: ApiGetListingsPayload, options?: { enabled?: boolean }) {
  return useQuery({
    ...listingsPageQuery(params),
    refetchOnMount: askUnlessServerAnswered,
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}
